/**
 * KundenKiosk — Apple-style Tablet-Wizard für Auftragsannahme
 * Tablet-First: 1280×800, Touch-Targets ≥ 60×60 px
 * Schritte: Auftragstyp → Fahrzeug → Kontakt → Schadensart → Details → Unterschrift → Bewertung → Social Media → Fertig
 */

import { useState, useCallback } from 'react';
import { useLocation } from 'wouter';
import { AnimatePresence } from 'framer-motion';
import {
  ArrowLeft, ArrowRight, User, Car,
  CheckCircle2, Shield, X, FileText,
  Layers, Wrench, Scale, Clipboard, Hash,
  Info, Clock,
} from 'lucide-react';
import { toast } from 'sonner';
import { AuftragsTypTile } from '@/components/kiosk/AuftragsTypTile';
import { FahrzeugscheinScanStep } from '@/components/kiosk/FahrzeugscheinScanStep';
import { FuehrerscheinScanStep } from '@/components/kiosk/FuehrerscheinScanStep';
import { UnfallberichtScanStep } from '@/components/kiosk/UnfallberichtScanStep';
import { BankkarteScanStep } from '@/components/kiosk/BankkarteScanStep';
import { SchadenFotosStep } from '@/components/kiosk/SchadenFotosStep';
import { KioskStepWrapper } from '@/components/kiosk/KioskStepWrapper';
import { DatenBestaetigenStep } from '@/components/kiosk/DatenBestaetigenStep';
import { GutachtenVersandStep, canProceedGutachtenVersand } from '@/components/kiosk/GutachtenVersandStep';
import type { GutachtenVersand } from '@/components/kiosk/GutachtenVersandStep';
import { ZusatzfragenStep, type ZusatzfragenData } from '@/components/kiosk/ZusatzfragenStep';
import { DemontageStep } from '@/components/kiosk/DemontageStep';
import { UnterschriftenStep } from '@/components/kiosk/UnterschriftenStep';
import { AnwaltAuswahlStep } from '@/components/kiosk/AnwaltAuswahlStep';
import {
  AUFTRAGSTYP_OPTIONS,
  getStepsForTyp,
  type AuftragsTyp,
} from '@/lib/kiosk-flow';

// ─── Types ────────────────────────────────────────────────────────────────────

type KioskStep = 'auftragstyp' | 'fahrzeugschein' | 'unfallbericht' | 'fuehrerschein' | 'fahrzeug' | 'schadenfotos' | 'kontakt' | 'bankkarte' | 'gutachtenversand' | 'datenbestaetigen' | 'zusatzfragen' | 'demontage' | 'details' | 'anwalt' | 'auftrag' | 'bewertung' | 'instagram' | 'complete';
type Schadensart = 'gutachten' | 'autoglas' | 'werkstatt' | '';
type PlaketteWunsch = 'ja' | 'nein' | 'vorhanden' | '';

interface KioskFormData {
  // Auftragstyp (neue Wizard-Architektur)
  auftragstyp: AuftragsTyp | '';
  // Fahrzeugschein OCR (Schritt 2)
  fahrzeugscheinUrl: string;
  fin: string;
  halter: string;
  erstzulassung: string;
  hsn: string;
  tsn: string;
  kioskSessionId: string;
  // Führerschein OCR (Schritt 3)
  fuehrerscheinVorname: string;
  fuehrerscheinNachname: string;
  fuehrerscheinGeburtsdatum: string;
  fuehrerscheinKlassen: { klasse: string; ausstellungsdatum: string }[];
  // Fahrzeug
  kennzeichen: string;
  hersteller: string;
  modell: string;
  // Kontakt
  vorname: string;
  nachname: string;
  anschrift: string;
  telefon: string;
  email: string;
  // Schadensart
  schadensart: Schadensart;
  // Details
  schadenBeschreibung: string;
  gruenePlakette: PlaketteWunsch;
  // Versicherung (Schritt 7 — DatenBestaetigenStep)
  eigeneVersicherung: string;
  vollkasko: boolean;
  gegnerVersicherung: string;
  vsNummer: string;
  // Bank (Schritt 7 — DatenBestaetigenStep)
  iban: string;
  karteninhaber: string;
  bic: string;
  // Zusatzfragen (Schritt 8)
  istEigentuemer: boolean;
  eigentuemer: string;
  istFinanziert: boolean;
  finanzierungsbank: string;
  hatRechtsschutz: boolean;
  rechtsschutzVersicherer: string;
  rechtsschutzVsNummer: string;
  istBevollmaechtigter: boolean;
  bevollmaechtigterName: string;
  // Konditionelle Schritt-13-Felder (Phase 3: Demontage, Mietwagen)
  demontageNoetig: boolean;
  verkehrssicherheitMitarbeiterId: string;
  verkehrssicherheitMitarbeiterName: string;
  ersatzfahrzeugGewaehlt: boolean;
  // Unfallbericht-Scan (Schritt 3, nur bei unfall)
  unfallberichtUrls: string[];
  // Schadenfotos (Schritt 5)
  schadenfotosUrls: string[];
  // Gutachten-Versandart (Schritt 12)
  gutachtenVersand: GutachtenVersand;
  // Unterschriften (Schritt 13)
  unterschriften: Record<string, string>;
  // Anwalt-Auswahl + Kommunikationsmodus (Schritte 10+11)
  anwaltId: string;
  anwaltName: string;
  anwaltKontaktModus: 'telefon' | 'direkt' | 'beide' | '';
  // Bewertung & Instagram
  bewertungBestaetigt: boolean;
  instagramEinwilligung: boolean;
  instagramHandle: string;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const SCHADENSARTEN: { id: Schadensart; label: string; desc: string; icon: React.ReactNode; color: string; bg: string }[] = [
  {
    id: 'gutachten',
    label: 'Kfz-Gutachten',
    desc: 'Unfallschaden, Wertgutachten, Haftpflicht',
    icon: <Scale size={24} />,
    color: '#C41E3A',
    bg: '#FEE2E2',
  },
  {
    id: 'autoglas',
    label: 'Autoglas / Scheibe',
    desc: 'Windschutzscheibe, Seitenscheibe, Heckscheibe',
    icon: <Layers size={24} />,
    color: '#059669',
    bg: '#D1FAE5',
  },
  {
    id: 'werkstatt',
    label: 'Werkstatt / Reparatur',
    desc: 'Karosserie, Lackierung, Mechanik',
    icon: <Wrench size={24} />,
    color: '#1D4ED8',
    bg: '#DBEAFE',
  },
];

const PLAKETTE_OPTIONEN: { value: PlaketteWunsch; label: string; desc: string }[] = [
  { value: 'ja',        label: 'Ja, gewünscht',         desc: 'Wird bei der Montage angebracht' },
  { value: 'nein',      label: 'Nein, nicht nötig',     desc: 'Keine Plakette erforderlich' },
  { value: 'vorhanden', label: 'Bereits vorhanden',     desc: 'Plakette ist schon am Fahrzeug' },
];

const BRAND_BLUE = '#1E5FB7';
const BRAND_GREEN = '#3DB54A';
const KOOP_ROT = '#C41E3A';

// ─── Auftragsnummer generieren ────────────────────────────────────────────────

function generateAuftragsnummer(schadensart: Schadensart): string {
  const prefix = schadensart === 'gutachten' ? 'GU' : schadensart === 'autoglas' ? 'AG' : 'WK';
  const date = new Date();
  const dateStr = `${date.getFullYear()}${String(date.getMonth() + 1).padStart(2, '0')}${String(date.getDate()).padStart(2, '0')}`;
  const rand = Math.floor(Math.random() * 900 + 100);
  return `${prefix}-${dateStr}-${rand}`;
}

// ─── Eingabe-Komponente ───────────────────────────────────────────────────────

function KioskInput({
  label, value, onChange, type = 'text', placeholder, required,
}: {
  label: string; value: string; onChange: (v: string) => void;
  type?: string; placeholder?: string; required?: boolean;
}) {
  return (
    <div>
      <label className="block text-[13px] font-semibold text-gray-600 mb-1.5">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      <input
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 bg-white text-[15px] focus:outline-none focus:ring-2 focus:ring-[#1E5FB7]/30 focus:border-[#1E5FB7] transition-all"
        style={{ minHeight: 48 }}
      />
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function KundenKiosk() {
  const [, setLocation] = useLocation();
  const [step, setStep] = useState<KioskStep>('auftragstyp');
  const [direction, setDirection] = useState(1);
  const [auftragsnummer, setAuftragsnummer] = useState('');
  const [formData, setFormData] = useState<KioskFormData>({
    auftragstyp: '',
    fahrzeugscheinUrl: '',
    fin: '',
    halter: '',
    erstzulassung: '',
    hsn: '',
    tsn: '',
    kioskSessionId: '',
    fuehrerscheinVorname: '',
    fuehrerscheinNachname: '',
    fuehrerscheinGeburtsdatum: '',
    fuehrerscheinKlassen: [],
    kennzeichen: '',
    hersteller: '',
    modell: '',
    vorname: '',
    nachname: '',
    anschrift: '',
    telefon: '',
    email: '',
    schadensart: '',
    schadenBeschreibung: '',
    gruenePlakette: '',
    eigeneVersicherung: '',
    vollkasko: false,
    gegnerVersicherung: '',
    vsNummer: '',
    iban: '',
    karteninhaber: '',
    bic: '',
    istEigentuemer: true,
    eigentuemer: '',
    istFinanziert: false,
    finanzierungsbank: '',
    hatRechtsschutz: false,
    rechtsschutzVersicherer: '',
    rechtsschutzVsNummer: '',
    istBevollmaechtigter: true,
    bevollmaechtigterName: '',
    demontageNoetig: false,
    verkehrssicherheitMitarbeiterId: '',
    verkehrssicherheitMitarbeiterName: '',
    ersatzfahrzeugGewaehlt: false,
    unfallberichtUrls: [],
    schadenfotosUrls: [],
    gutachtenVersand: '',
    unterschriften: {},
    anwaltId: '',
    anwaltName: '',
    anwaltKontaktModus: '',
    bewertungBestaetigt: false,
    instagramEinwilligung: false,
    instagramHandle: '',
  });

  const STEPS = getStepsForTyp(formData.auftragstyp);
  const currentStepIndex = STEPS.findIndex(s => s.id === step);

  const set = useCallback(<K extends keyof KioskFormData>(key: K, val: KioskFormData[K]) => {
    setFormData(prev => ({ ...prev, [key]: val }));
  }, []);

  const canProceed = (): boolean => {
    switch (step) {
      case 'auftragstyp':       return formData.auftragstyp !== '';
      case 'fahrzeugschein':    return true;
      case 'unfallbericht':    return true;
      case 'fahrzeug':          return formData.kennzeichen.trim().length > 0;
      case 'schadenfotos':      return formData.schadenfotosUrls.length >= 1;
      case 'kontakt':           return formData.nachname.trim().length > 0 && formData.telefon.trim().length > 0;
      case 'gutachtenversand':  return canProceedGutachtenVersand(formData.gutachtenVersand);
      case 'datenbestaetigen':  return true;
      case 'zusatzfragen':      return true;
      case 'details':
        if (formData.schadensart === 'autoglas') return formData.gruenePlakette !== '';
        return true;
      case 'anwalt':    return formData.anwaltId !== '' && formData.anwaltKontaktModus !== '';
      case 'auftrag':   return true;
      case 'bewertung':  return true;
      case 'instagram':  return true;
      default: return true;
    }
  };

  const nextStep = () => {
    if (!canProceed()) {
      toast.error('Bitte füllen Sie alle Pflichtfelder aus');
      return;
    }
    if (step === 'details') {
      const nr = generateAuftragsnummer(formData.schadensart);
      setAuftragsnummer(nr);
      try {
        const existing = JSON.parse(localStorage.getItem('kiosk_auftraege_v1') || '[]');
        existing.push({ ...formData, auftragsnummer: nr, erstellt: new Date().toISOString(), status: 'neu' });
        localStorage.setItem('kiosk_auftraege_v1', JSON.stringify(existing));
      } catch {}
      toast.success('Auftrag angelegt!', { description: `Auftragsnummer: ${nr}` });
    }
    if (step === 'instagram') {
      try {
        const auftraege = JSON.parse(localStorage.getItem('kiosk_auftraege_v1') || '[]');
        if (auftraege.length > 0) {
          auftraege[auftraege.length - 1].bewertungBestaetigt = formData.bewertungBestaetigt;
          auftraege[auftraege.length - 1].instagramEinwilligung = formData.instagramEinwilligung;
          auftraege[auftraege.length - 1].instagramHandle = formData.instagramHandle;
          localStorage.setItem('kiosk_auftraege_v1', JSON.stringify(auftraege));
        }
      } catch {}
    }
    const idx = STEPS.findIndex(s => s.id === step);
    if (idx < STEPS.length - 1) {
      setDirection(1);
      setStep(STEPS[idx + 1].id as KioskStep);
    }
  };

  const prevStep = () => {
    const idx = STEPS.findIndex(s => s.id === step);
    if (idx > 0) {
      setDirection(-1);
      setStep(STEPS[idx - 1].id as KioskStep);
    }
  };

  const exitKiosk = () => setLocation('/');

  const schadensartConfig = SCHADENSARTEN.find(s => s.id === formData.schadensart);

  // Sync auftragstyp → schadensart when tile is selected
  const selectAuftragstyp = (typ: AuftragsTyp) => {
    set('auftragstyp', typ);
    if (typ === 'autoglas') set('schadensart', 'autoglas');
    else if (typ === 'unfall') set('schadensart', 'gutachten');
  };

  return (
    <div className="fixed inset-0 z-[100] bg-[#F8F7F4] flex flex-col overflow-hidden">

      {/* ── Header ── */}
      <div className="flex-shrink-0 flex items-center justify-between px-6 py-4 bg-white border-b border-gray-200 shadow-sm">
        <div className="flex items-center gap-4">
          <div
            className="w-12 h-12 rounded-2xl flex items-center justify-center shadow-md flex-shrink-0"
            style={{ background: `linear-gradient(135deg, ${BRAND_BLUE}, #1A4F9A)` }}
          >
            <Clipboard size={22} className="text-white" />
          </div>
          <div>
            <h1 className="text-[16px] font-bold text-gray-900 leading-tight">Auftragsannahme</h1>
            <p className="text-[12px] text-gray-500">Gutachter KOOP — Kunden-Kiosk</p>
          </div>
        </div>

        {/* Progress Dots */}
        <div className="hidden sm:flex items-center gap-1.5">
          {STEPS.map((s, i) => (
            <div key={s.id} className="flex items-center gap-1.5">
              <div
                className="transition-all rounded-full"
                style={{
                  width: i === currentStepIndex ? 12 : 8,
                  height: i === currentStepIndex ? 12 : 8,
                  background: i < currentStepIndex ? BRAND_GREEN : i === currentStepIndex ? BRAND_BLUE : '#D1D5DB',
                }}
              />
              {i < STEPS.length - 1 && (
                <div
                  className="h-0.5 rounded-full transition-colors"
                  style={{ width: 20, background: i < currentStepIndex ? BRAND_GREEN : '#E5E7EB' }}
                />
              )}
            </div>
          ))}
        </div>

        {/* Step Label + Close */}
        <div className="flex items-center gap-3">
          <span className="text-[12px] font-semibold text-gray-500">
            {Math.max(currentStepIndex, 0) + 1} / {STEPS.length}
          </span>
          <button
            onClick={exitKiosk}
            className="w-10 h-10 rounded-xl hover:bg-gray-100 flex items-center justify-center transition-colors"
          >
            <X size={20} className="text-gray-400" />
          </button>
        </div>
      </div>

      {/* ── Animated Step Content ── */}
      <div className="flex-1 overflow-hidden flex items-center justify-center p-6">
        <div className="w-full max-w-2xl">
          <AnimatePresence mode="wait" initial={false}>
            <KioskStepWrapper stepKey={step} direction={direction}>

              {/* AUFTRAGSTYP — Schritt 1 */}
              {step === 'auftragstyp' && (
                <div className="space-y-8">
                  <div className="text-center">
                    <div
                      className="w-20 h-20 rounded-3xl flex items-center justify-center mx-auto mb-5 shadow-xl"
                      style={{ background: `linear-gradient(135deg, ${BRAND_BLUE}, #1A4F9A)` }}
                    >
                      <FileText size={36} className="text-white" />
                    </div>
                    <h2 className="text-[30px] font-bold text-gray-900 leading-tight">Willkommen!</h2>
                    <p className="text-[16px] text-gray-500 mt-3 leading-relaxed max-w-md mx-auto">
                      Was können wir für Sie tun? Wählen Sie bitte Ihren Auftragstyp.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    {AUFTRAGSTYP_OPTIONS.map(opt => (
                      <AuftragsTypTile
                        key={opt.id}
                        option={opt}
                        selected={formData.auftragstyp === opt.id}
                        onSelect={() => selectAuftragstyp(opt.id)}
                      />
                    ))}
                  </div>

                  <div className="flex items-center justify-center gap-6 text-[13px] text-gray-500 pt-2">
                    <div className="flex items-center gap-2">
                      <Clock size={15} className="text-gray-400" />
                      <span>ca. 3 Minuten</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Shield size={15} className="text-gray-400" />
                      <span>Sicher & vertraulich</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle2 size={15} className="text-gray-400" />
                      <span>Sofort bearbeitet</span>
                    </div>
                  </div>
                </div>
              )}

              {/* FAHRZEUGSCHEIN-SCAN — Schritt 2 */}
              {step === 'fahrzeugschein' && (
                <FahrzeugscheinScanStep
                  onComplete={(felder, fahrzeugscheinUrl, sessionId) => {
                    setFormData(prev => ({
                      ...prev,
                      fahrzeugscheinUrl,
                      fin: felder.fin,
                      halter: felder.halter,
                      erstzulassung: felder.erstzulassung,
                      hsn: felder.hsn,
                      tsn: felder.tsn,
                      // Pre-fill kennzeichen if not already set by user
                      kennzeichen: prev.kennzeichen || felder.kennzeichen,
                      kioskSessionId: sessionId,
                    }));
                    const idx = STEPS.findIndex(s => s.id === 'fahrzeugschein');
                    if (idx < STEPS.length - 1) {
                      setDirection(1);
                      setStep(STEPS[idx + 1].id as KioskStep);
                    }
                  }}
                  onBack={prevStep}
                />
              )}

              {/* UNFALLBERICHT-SCAN — Schritt 3 (nur bei auftragstyp === 'unfall') */}
              {step === 'unfallbericht' && (
                <UnfallberichtScanStep
                  sessionId={formData.kioskSessionId}
                  onComplete={(felder, urls, sessionId) => {
                    setFormData(prev => ({
                      ...prev,
                      unfallberichtUrls: urls,
                      // Pre-fill gegner fields from OCR
                      gegnerVersicherung: prev.gegnerVersicherung || felder.versicherungGegner,
                      vsNummer: prev.vsNummer || felder.vsNummerGegner,
                      kioskSessionId: sessionId || prev.kioskSessionId,
                    }));
                    const idx = STEPS.findIndex(s => s.id === 'unfallbericht');
                    if (idx < STEPS.length - 1) {
                      setDirection(1);
                      setStep(STEPS[idx + 1].id as KioskStep);
                    }
                  }}
                  onSkip={() => {
                    const idx = STEPS.findIndex(s => s.id === 'unfallbericht');
                    if (idx < STEPS.length - 1) {
                      setDirection(1);
                      setStep(STEPS[idx + 1].id as KioskStep);
                    }
                  }}
                  onBack={prevStep}
                />
              )}

              {/* FÜHRERSCHEIN-SCAN — Schritt 4 */}
              {step === 'fuehrerschein' && (
                <FuehrerscheinScanStep
                  sessionId={formData.kioskSessionId}
                  onComplete={ergebnis => {
                    setFormData(prev => ({
                      ...prev,
                      fuehrerscheinVorname: ergebnis.vorderseite.vorname,
                      fuehrerscheinNachname: ergebnis.vorderseite.nachname,
                      fuehrerscheinGeburtsdatum: ergebnis.vorderseite.geburtsdatum,
                      fuehrerscheinKlassen: ergebnis.klassen,
                      kioskSessionId: prev.kioskSessionId || ergebnis.sessionId,
                    }));
                    const idx = STEPS.findIndex(s => s.id === 'fuehrerschein');
                    if (idx < STEPS.length - 1) {
                      setDirection(1);
                      setStep(STEPS[idx + 1].id as KioskStep);
                    }
                  }}
                  onSkip={() => {
                    const idx = STEPS.findIndex(s => s.id === 'fuehrerschein');
                    if (idx < STEPS.length - 1) {
                      setDirection(1);
                      setStep(STEPS[idx + 1].id as KioskStep);
                    }
                  }}
                  onBack={prevStep}
                />
              )}

              {/* SCHADENFOTOS — Schritt 5 */}
              {step === 'schadenfotos' && (
                <SchadenFotosStep
                  sessionId={formData.kioskSessionId}
                  onComplete={(urls) => {
                    set('schadenfotosUrls', urls);
                    const idx = STEPS.findIndex(s => s.id === 'schadenfotos');
                    if (idx < STEPS.length - 1) {
                      setDirection(1);
                      setStep(STEPS[idx + 1].id as KioskStep);
                    }
                  }}
                  onBack={prevStep}
                />
              )}

              {/* FAHRZEUG */}
              {step === 'fahrzeug' && (
                <div className="space-y-5">
                  <div className="text-center mb-6">
                    <div
                      className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-3 shadow-md"
                      style={{ background: BRAND_BLUE }}
                    >
                      <Car size={26} className="text-white" />
                    </div>
                    <h2 className="text-[22px] font-bold text-gray-900">Fahrzeugdaten</h2>
                    <p className="text-[14px] text-gray-500 mt-1">Angaben zum betroffenen Fahrzeug</p>
                  </div>
                  <KioskInput
                    label="Kennzeichen"
                    value={formData.kennzeichen}
                    onChange={v => set('kennzeichen', v.toUpperCase())}
                    placeholder="MK-AB 123"
                    required
                  />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <KioskInput
                      label="Hersteller"
                      value={formData.hersteller}
                      onChange={v => set('hersteller', v)}
                      placeholder="Volkswagen"
                    />
                    <KioskInput
                      label="Modell"
                      value={formData.modell}
                      onChange={v => set('modell', v)}
                      placeholder="Golf 8"
                    />
                  </div>
                </div>
              )}

              {/* KONTAKT */}
              {step === 'kontakt' && (
                <div className="space-y-5">
                  <div className="text-center mb-6">
                    <div
                      className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-3 shadow-md"
                      style={{ background: BRAND_BLUE }}
                    >
                      <User size={26} className="text-white" />
                    </div>
                    <h2 className="text-[22px] font-bold text-gray-900">Ihre Kontaktdaten</h2>
                    <p className="text-[14px] text-gray-500 mt-1">Damit wir Sie erreichen können</p>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <KioskInput label="Vorname" value={formData.vorname} onChange={v => set('vorname', v)} placeholder="Max" />
                    <KioskInput label="Nachname" value={formData.nachname} onChange={v => set('nachname', v)} placeholder="Mustermann" required />
                  </div>
                  <KioskInput label="Telefon" value={formData.telefon} onChange={v => set('telefon', v)} type="tel" placeholder="0151 12345678" required />
                  <KioskInput label="E-Mail (optional)" value={formData.email} onChange={v => set('email', v)} type="email" placeholder="max@email.de" />
                </div>
              )}

              {/* BANKKARTE-SCAN — Schritt 5 */}
              {step === 'bankkarte' && (
                <BankkarteScanStep
                  sessionId={formData.kioskSessionId}
                  onComplete={(felder, sessionId) => {
                    setFormData(prev => ({
                      ...prev,
                      iban: felder.iban || prev.iban,
                      karteninhaber: felder.karteninhaber || prev.karteninhaber,
                      kioskSessionId: sessionId || prev.kioskSessionId,
                    }));
                    const idx = STEPS.findIndex(s => s.id === 'bankkarte');
                    if (idx < STEPS.length - 1) {
                      setDirection(1);
                      setStep(STEPS[idx + 1].id as KioskStep);
                    }
                  }}
                  onSkip={() => {
                    const idx = STEPS.findIndex(s => s.id === 'bankkarte');
                    if (idx < STEPS.length - 1) {
                      setDirection(1);
                      setStep(STEPS[idx + 1].id as KioskStep);
                    }
                  }}
                  onBack={prevStep}
                />
              )}

              {/* GUTACHTEN-VERSAND — Schritt 12 */}
              {step === 'gutachtenversand' && (
                <GutachtenVersandStep
                  value={formData.gutachtenVersand}
                  onChange={v => set('gutachtenVersand', v)}
                />
              )}

              {/* DATEN BESTÄTIGEN — Schritt 7 */}
              {step === 'datenbestaetigen' && (
                <DatenBestaetigenStep
                  formData={formData}
                  onComplete={daten => {
                    setFormData(prev => ({
                      ...prev,
                      // Sync confirmed data back into formData
                      vorname: daten.halter.name.split(' ')[0] ?? prev.vorname,
                      nachname: daten.halter.name.split(' ').slice(1).join(' ') || prev.nachname,
                      anschrift: daten.halter.anschrift,
                      telefon: daten.halter.telefon,
                      email: daten.halter.email,
                      kennzeichen: daten.fahrzeug.kennzeichen,
                      fin: daten.fahrzeug.fin,
                      erstzulassung: daten.fahrzeug.erstzulassung,
                      eigeneVersicherung: daten.versicherung.eigeneVersicherung,
                      vollkasko: daten.versicherung.vollkasko,
                      gegnerVersicherung: daten.versicherung.gegnerVersicherung,
                      vsNummer: daten.versicherung.vsNummer,
                      iban: daten.bank.iban,
                      bic: daten.bank.bic,
                    }));
                    const idx = STEPS.findIndex(s => s.id === 'datenbestaetigen');
                    if (idx < STEPS.length - 1) {
                      setDirection(1);
                      setStep(STEPS[idx + 1].id as KioskStep);
                    }
                  }}
                  onBack={prevStep}
                />
              )}

              {/* ZUSATZFRAGEN — Schritt 8 */}
              {step === 'zusatzfragen' && (
                <ZusatzfragenStep
                  data={{
                    istEigentuemer: formData.istEigentuemer,
                    eigentuemer: formData.eigentuemer,
                    istFinanziert: formData.istFinanziert,
                    finanzierungsbank: formData.finanzierungsbank,
                    hatRechtsschutz: formData.hatRechtsschutz,
                    rechtsschutzVersicherer: formData.rechtsschutzVersicherer,
                    rechtsschutzVsNummer: formData.rechtsschutzVsNummer,
                    istBevollmaechtigter: formData.istBevollmaechtigter,
                    bevollmaechtigterName: formData.bevollmaechtigterName,
                  }}
                  onChange={(d: ZusatzfragenData) => setFormData(prev => ({ ...prev, ...d }))}
                  onNext={nextStep}
                  onBack={prevStep}
                />
              )}

              {/* DEMONTAGE — Schritt 9, nur Unfall-Flow */}
              {step === 'demontage' && (
                <DemontageStep
                  onComplete={data => {
                    setFormData(prev => ({
                      ...prev,
                      demontageNoetig: data.demontageNoetig,
                      verkehrssicherheitMitarbeiterId: data.verkehrssicherheitMitarbeiterId,
                      verkehrssicherheitMitarbeiterName: data.verkehrssicherheitMitarbeiterName,
                      ersatzfahrzeugGewaehlt: data.ersatzfahrzeugGewaehlt,
                    }));
                    const idx = STEPS.findIndex(s => s.id === 'demontage');
                    if (idx < STEPS.length - 1) {
                      setDirection(1);
                      setStep(STEPS[idx + 1].id as KioskStep);
                    }
                  }}
                  onBack={prevStep}
                />
              )}

              {/* DETAILS */}
              {step === 'details' && (
                <div className="space-y-5">
                  <div className="text-center mb-6">
                    {schadensartConfig && (
                      <div
                        className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-3 shadow-md"
                        style={{ background: schadensartConfig.color }}
                      >
                        {schadensartConfig.icon}
                      </div>
                    )}
                    <h2 className="text-[22px] font-bold text-gray-900">Details zum Schaden</h2>
                    <p className="text-[14px] text-gray-500 mt-1">
                      {schadensartConfig?.label} · {formData.kennzeichen || 'Fahrzeug'}
                    </p>
                  </div>

                  {formData.schadensart === 'autoglas' && (
                    <div className="bg-white rounded-2xl border-2 border-emerald-200 p-5 shadow-sm">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
                          <Shield size={20} className="text-emerald-600" />
                        </div>
                        <div>
                          <h3 className="text-[14px] font-bold text-gray-900">Grüne Plakette gewünscht?</h3>
                          <p className="text-[11px] text-gray-500">Umweltplakette für Ihr Fahrzeug</p>
                        </div>
                        {formData.gruenePlakette && (
                          <span className="ml-auto text-[11px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">✓ Gewählt</span>
                        )}
                      </div>
                      <div className="grid grid-cols-1 gap-2">
                        {PLAKETTE_OPTIONEN.map(opt => (
                          <button
                            key={opt.value}
                            onClick={() => set('gruenePlakette', opt.value)}
                            className="flex items-center gap-3 p-3 rounded-xl border-2 text-left transition-all"
                            style={{
                              minHeight: 60,
                              borderColor: formData.gruenePlakette === opt.value ? '#10B981' : '#E5E7EB',
                              background: formData.gruenePlakette === opt.value ? '#ECFDF5' : '#F9FAFB',
                            }}
                          >
                            <div
                              className="w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0"
                              style={{ borderColor: formData.gruenePlakette === opt.value ? '#10B981' : '#D1D5DB' }}
                            >
                              {formData.gruenePlakette === opt.value && (
                                <div className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
                              )}
                            </div>
                            <div>
                              <div className={`text-[13px] font-semibold ${formData.gruenePlakette === opt.value ? 'text-emerald-700' : 'text-gray-700'}`}>
                                {opt.label}
                              </div>
                              <div className="text-[11px] text-gray-500">{opt.desc}</div>
                            </div>
                          </button>
                        ))}
                      </div>
                      <p className="mt-2 text-[11px] text-gray-400 flex items-center gap-1">
                        <span className="text-red-500">*</span> Pflichtfeld
                      </p>
                    </div>
                  )}

                  <div>
                    <label className="block text-[13px] font-semibold text-gray-600 mb-1.5">
                      Kurzbeschreibung des Schadens
                    </label>
                    <textarea
                      value={formData.schadenBeschreibung}
                      onChange={e => set('schadenBeschreibung', e.target.value)}
                      className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 bg-white text-[15px] focus:outline-none focus:ring-2 focus:ring-[#1E5FB7]/30 focus:border-[#1E5FB7] min-h-[100px] resize-none transition-all"
                      placeholder="Beschreiben Sie kurz den Schaden an Ihrem Fahrzeug..."
                    />
                  </div>
                </div>
              )}

              {/* ANWALT-AUSWAHL + KOMMUNIKATIONSMODUS — Schritte 10+11 */}
              {step === 'anwalt' && (
                <AnwaltAuswahlStep
                  onComplete={(id, name, modus) => {
                    setFormData(prev => ({
                      ...prev,
                      anwaltId: id,
                      anwaltName: name,
                      anwaltKontaktModus: modus,
                    }));
                    try {
                      const auftraege = JSON.parse(localStorage.getItem('kiosk_auftraege_v1') || '[]');
                      if (auftraege.length > 0) {
                        auftraege[auftraege.length - 1].anwaltId = id;
                        auftraege[auftraege.length - 1].anwaltName = name;
                        auftraege[auftraege.length - 1].anwaltKontaktModus = modus;
                        localStorage.setItem('kiosk_auftraege_v1', JSON.stringify(auftraege));
                      }
                    } catch {}
                    const idx = STEPS.findIndex(s => s.id === 'anwalt');
                    if (idx < STEPS.length - 1) {
                      setDirection(1);
                      setStep(STEPS[idx + 1].id as KioskStep);
                    }
                  }}
                  onBack={prevStep}
                />
              )}

              {/* AUFTRAG / UNTERSCHRIFTEN — Schritt 13 */}
              {step === 'auftrag' && (
                <UnterschriftenStep
                  sessionId={formData.kioskSessionId}
                  kundenName={`${formData.vorname} ${formData.nachname}`.trim()}
                  kennzeichen={formData.kennzeichen}
                  demontageNoetig={formData.demontageNoetig}
                  ersatzfahrzeugGewaehlt={formData.ersatzfahrzeugGewaehlt}
                  anwaltName={formData.anwaltName || undefined}
                  onComplete={(sigs) => {
                    set('unterschriften', sigs);
                    const idx = STEPS.findIndex(s => s.id === 'auftrag');
                    if (idx < STEPS.length - 1) {
                      setDirection(1);
                      setStep(STEPS[idx + 1].id as KioskStep);
                    }
                  }}
                  onBack={prevStep}
                />
              )}

              {/* BEWERTUNG */}
              {step === 'bewertung' && (
                <div className="space-y-6">
                  <div className="text-center">
                    <div className="w-20 h-20 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4 shadow-lg">
                      <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="#F59E0B" stroke="none"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
                    </div>
                    <h2 className="text-[26px] font-bold text-gray-900">Waren Sie zufrieden?</h2>
                    <p className="text-[16px] text-gray-500 mt-2 leading-relaxed max-w-sm mx-auto">
                      Hinterlassen Sie uns eine Google-Bewertung — das dauert nur 1 Minute!
                    </p>
                  </div>
                  <a
                    href="https://www.google.com/maps/place/SV+B%C3%BCro+Koop/@51.2200,7.6300,15z"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-3 w-full py-5 rounded-2xl text-[16px] font-bold text-white shadow-lg hover:opacity-90 transition-all"
                    style={{ background: 'linear-gradient(135deg, #4285F4, #34A853)', minHeight: 64 }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="white" stroke="none"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
                    Jetzt auf Google bewerten
                  </a>
                  <button
                    onClick={() => set('bewertungBestaetigt', !formData.bewertungBestaetigt)}
                    className="w-full flex items-center gap-3 p-4 rounded-2xl border-2 transition-all"
                    style={{
                      minHeight: 60,
                      borderColor: formData.bewertungBestaetigt ? '#10B981' : '#E5E7EB',
                      background: formData.bewertungBestaetigt ? '#ECFDF5' : '#FFFFFF',
                    }}
                  >
                    <div
                      className="w-6 h-6 rounded-lg border-2 flex items-center justify-center flex-shrink-0 transition-all"
                      style={{
                        borderColor: formData.bewertungBestaetigt ? '#10B981' : '#D1D5DB',
                        background: formData.bewertungBestaetigt ? '#10B981' : 'transparent',
                      }}
                    >
                      {formData.bewertungBestaetigt && <CheckCircle2 size={16} className="text-white" />}
                    </div>
                    <span className="text-[14px] font-medium text-gray-700">Ich habe eine Bewertung hinterlassen</span>
                  </button>
                  <p className="text-[12px] text-gray-400 text-center">Optional — Sie können diesen Schritt auch überspringen</p>
                  <div className="flex items-center justify-between pt-2">
                    <button onClick={prevStep} className="flex items-center gap-2 px-6 py-3 rounded-xl border-2 border-gray-200 hover:bg-gray-50 transition-all text-[14px] font-semibold text-gray-600" style={{ minHeight: 52 }}>
                      <ArrowLeft size={16} /> Zurück
                    </button>
                    <button onClick={nextStep} className="flex items-center gap-2 px-7 py-3 rounded-xl text-[14px] font-bold text-white shadow-md hover:opacity-90 transition-all" style={{ background: `linear-gradient(135deg, ${BRAND_BLUE}, #1A4F9A)`, minHeight: 52 }}>
                      Weiter <ArrowRight size={16} />
                    </button>
                  </div>
                </div>
              )}

              {/* INSTAGRAM */}
              {step === 'instagram' && (
                <div className="space-y-6">
                  <div className="text-center">
                    <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg" style={{ background: 'linear-gradient(135deg, #833AB4, #FD1D1D, #F77737)' }}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 24 24" fill="white" stroke="none"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
                    </div>
                    <h2 className="text-[24px] font-bold text-gray-900">Darf Ihr Fahrzeug ins Rampenlicht?</h2>
                    <p className="text-[15px] text-gray-500 mt-2 leading-relaxed max-w-sm mx-auto">
                      Wir würden gerne ein Foto Ihres Fahrzeugs auf unserem Instagram-Kanal teilen. Als Dankeschön erhalten Sie bei jedem Repost einen <strong className="text-emerald-600">20 € Tankgutschein</strong>!
                    </p>
                  </div>
                  <div className="space-y-3">
                    <button
                      onClick={() => set('instagramEinwilligung', true)}
                      className="w-full flex items-center gap-4 p-4 rounded-2xl border-2 transition-all text-left"
                      style={{
                        minHeight: 64,
                        borderColor: formData.instagramEinwilligung ? '#EC4899' : '#E5E7EB',
                        background: formData.instagramEinwilligung ? '#FDF2F8' : '#FFFFFF',
                      }}
                    >
                      <div
                        className="w-7 h-7 rounded-full border-2 flex items-center justify-center flex-shrink-0"
                        style={{
                          borderColor: formData.instagramEinwilligung ? '#EC4899' : '#D1D5DB',
                          background: formData.instagramEinwilligung ? '#EC4899' : 'transparent',
                        }}
                      >
                        {formData.instagramEinwilligung && <CheckCircle2 size={14} className="text-white" />}
                      </div>
                      <div>
                        <span className="text-[15px] font-semibold text-gray-800 block">Ja, ich stimme zu</span>
                        <span className="text-[12px] text-gray-500">Foto ohne personenbezogene Daten, Kennzeichen unkenntlich</span>
                      </div>
                    </button>
                    <button
                      onClick={() => { set('instagramEinwilligung', false); set('instagramHandle', ''); }}
                      className="w-full flex items-center gap-4 p-4 rounded-2xl border-2 transition-all text-left"
                      style={{
                        minHeight: 64,
                        borderColor: !formData.instagramEinwilligung ? '#6B7280' : '#E5E7EB',
                        background: !formData.instagramEinwilligung ? '#F9FAFB' : '#FFFFFF',
                      }}
                    >
                      <div
                        className="w-7 h-7 rounded-full border-2 flex items-center justify-center flex-shrink-0"
                        style={{
                          borderColor: !formData.instagramEinwilligung ? '#6B7280' : '#D1D5DB',
                          background: !formData.instagramEinwilligung ? '#6B7280' : 'transparent',
                        }}
                      >
                        {!formData.instagramEinwilligung && <X size={14} className="text-white" />}
                      </div>
                      <div>
                        <span className="text-[15px] font-semibold text-gray-800 block">Nein, danke</span>
                        <span className="text-[12px] text-gray-500">Weiter ohne Social Media Einwilligung</span>
                      </div>
                    </button>
                  </div>
                  {formData.instagramEinwilligung && (
                    <div className="bg-white rounded-2xl border-2 border-gray-200 p-4 space-y-3">
                      <label className="block text-[13px] font-semibold text-gray-600">
                        Ihr Instagram-Handle (optional)
                      </label>
                      <div className="flex items-center gap-2">
                        <span className="text-[16px] text-gray-400 font-medium">@</span>
                        <input
                          type="text"
                          value={formData.instagramHandle}
                          onChange={e => set('instagramHandle', e.target.value.replace('@', ''))}
                          placeholder="ihr_username"
                          className="flex-1 px-3 py-2.5 rounded-xl border-2 border-gray-200 bg-gray-50 text-[15px] focus:outline-none focus:ring-2 focus:ring-pink-300 focus:border-pink-400 transition-all"
                          style={{ minHeight: 48 }}
                        />
                      </div>
                      <p className="text-[11px] text-gray-400">Damit wir Sie beim Repost markieren können</p>
                    </div>
                  )}
                  <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 flex items-start gap-3">
                    <Shield size={16} className="text-blue-500 mt-0.5 flex-shrink-0" />
                    <p className="text-[12px] text-blue-700 leading-relaxed">
                      <strong>DSGVO-Hinweis:</strong> Es werden keine personenbezogenen Daten veröffentlicht. Das Kennzeichen wird immer unkenntlich gemacht.
                    </p>
                  </div>
                  <div className="flex items-center justify-between pt-2">
                    <button onClick={prevStep} className="flex items-center gap-2 px-6 py-3 rounded-xl border-2 border-gray-200 hover:bg-gray-50 transition-all text-[14px] font-semibold text-gray-600" style={{ minHeight: 52 }}>
                      <ArrowLeft size={16} /> Zurück
                    </button>
                    <button onClick={nextStep} className="flex items-center gap-2 px-7 py-3 rounded-xl text-[14px] font-bold text-white shadow-md hover:opacity-90 transition-all" style={{ background: `linear-gradient(135deg, ${BRAND_BLUE}, #1A4F9A)`, minHeight: 52 }}>
                      Abschließen <ArrowRight size={16} />
                    </button>
                  </div>
                </div>
              )}

              {/* COMPLETE */}
              {step === 'complete' && (
                <div className="text-center space-y-6">
                  <div
                    className="w-24 h-24 rounded-full flex items-center justify-center mx-auto shadow-lg"
                    style={{ background: '#ECFDF5' }}
                  >
                    <CheckCircle2 size={50} className="text-emerald-600" />
                  </div>
                  <div>
                    <h2 className="text-[28px] font-bold text-gray-900">Auftrag aufgenommen!</h2>
                    <p className="text-[16px] text-gray-500 mt-3 leading-relaxed max-w-sm mx-auto">
                      Ihre Daten wurden erfolgreich aufgenommen. Ein Mitarbeiter wird sich in Kürze bei Ihnen melden.
                    </p>
                  </div>
                  <div className="bg-white rounded-2xl border-2 border-gray-200 p-6 shadow-sm">
                    <div className="flex items-center gap-3 mb-3">
                      <Hash size={18} className="text-gray-400" />
                      <span className="text-[13px] font-semibold text-gray-600">Ihre Auftragsnummer</span>
                    </div>
                    <div className="text-[30px] font-bold font-mono tracking-widest" style={{ color: BRAND_BLUE }}>
                      {auftragsnummer}
                    </div>
                    <p className="text-[12px] text-gray-400 mt-2">Bitte notieren Sie diese Nummer für Rückfragen</p>
                  </div>
                  <div className="bg-gray-50 rounded-2xl border border-gray-200 p-4 text-left space-y-2">
                    <h3 className="text-[13px] font-bold text-gray-700 mb-3">Zusammenfassung</h3>
                    <SummaryRow label="Name" value={`${formData.vorname} ${formData.nachname}`.trim() || '—'} />
                    <SummaryRow label="Telefon" value={formData.telefon || '—'} />
                    <SummaryRow label="Fahrzeug" value={`${formData.kennzeichen}${formData.modell ? ` · ${formData.hersteller} ${formData.modell}` : ''}`.trim() || '—'} />
                    <SummaryRow label="Schadensart" value={SCHADENSARTEN.find(s => s.id === formData.schadensart)?.label || '—'} />
                    <SummaryRow label="Gutachten-Versand" value={{ email: '📧 Per E-Mail', post: '📬 Per Post', beide: '🔄 Beides (E-Mail + Post)', '': '—' }[formData.gutachtenVersand]} />
                    {formData.schadensart === 'autoglas' && formData.gruenePlakette && (
                      <SummaryRow label="Grüne Plakette" value={PLAKETTE_OPTIONEN.find(o => o.value === formData.gruenePlakette)?.label || '—'} />
                    )}
                    {formData.schadenBeschreibung && (
                      <SummaryRow label="Beschreibung" value={formData.schadenBeschreibung} />
                    )}
                    {formData.anwaltName && (
                      <SummaryRow label="Anwalt" value={formData.anwaltName} />
                    )}
                    {formData.anwaltKontaktModus && (
                      <SummaryRow label="Kontaktmodus" value={{ telefon: '📞 Telefonisch', direkt: '⚡ Direkt', beide: '💬 Beide' }[formData.anwaltKontaktModus] ?? '—'} />
                    )}
                  </div>
                  <button
                    onClick={exitKiosk}
                    className="w-full py-4 rounded-2xl text-[16px] font-bold text-white shadow-lg hover:opacity-90 transition-all"
                    style={{ background: `linear-gradient(135deg, ${BRAND_BLUE}, #1A4F9A)`, minHeight: 64 }}
                  >
                    Zurück zum Dashboard
                  </button>
                </div>
              )}

            </KioskStepWrapper>
          </AnimatePresence>
        </div>
      </div>

      {/* ── Footer Navigation ── */}
      {!['complete', 'bewertung', 'instagram', 'auftrag', 'anwalt', 'fahrzeugschein', 'unfallbericht', 'fuehrerschein', 'schadenfotos', 'bankkarte', 'datenbestaetigen', 'zusatzfragen', 'demontage'].includes(step) && (
        <div className="flex-shrink-0 flex items-center justify-between px-6 py-4 bg-white border-t border-gray-200 shadow-[0_-2px_8px_rgba(0,0,0,0.06)]">
          <button
            onClick={step === 'auftragstyp' ? exitKiosk : prevStep}
            className="flex items-center gap-2 px-6 py-3 rounded-xl border-2 border-gray-200 hover:bg-gray-50 transition-all text-[14px] font-semibold text-gray-600"
            style={{ minHeight: 52, minWidth: 100 }}
          >
            <ArrowLeft size={16} />
            {step === 'auftragstyp' ? 'Abbrechen' : 'Zurück'}
          </button>

          {/* Step-Indikator Mobile */}
          <div className="flex items-center gap-1 sm:hidden">
            {STEPS.map((s, i) => (
              <div
                key={s.id}
                className="rounded-full transition-all"
                style={{
                  width: i === currentStepIndex ? 12 : 6,
                  height: 6,
                  background: i < currentStepIndex ? BRAND_GREEN : i === currentStepIndex ? BRAND_BLUE : '#D1D5DB',
                }}
              />
            ))}
          </div>

          <button
            onClick={nextStep}
            disabled={!canProceed()}
            className="flex items-center gap-2 px-7 py-3 rounded-xl text-[14px] font-bold text-white shadow-md hover:opacity-90 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ background: `linear-gradient(135deg, ${BRAND_BLUE}, #1A4F9A)`, minHeight: 52, minWidth: 100 }}
          >
            {step === 'details' ? 'Auftrag absenden' : 'Weiter'}
            <ArrowRight size={16} />
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Helper ───────────────────────────────────────────────────────────────────

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-start gap-3">
      <span className="text-[12px] text-gray-400 w-24 sm:w-28 flex-shrink-0 pt-0.5">{label}</span>
      <span className="text-[13px] font-medium text-gray-800 flex-1">{value}</span>
    </div>
  );
}
